import React, { useEffect, useRef, useState, useMemo } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  MapPin, Briefcase, Tag, MessageCircle, Sparkles, ChevronRight, ChevronLeft, Users,
  Target, Lightbulb, Flag, Building2, Share2, Copy, Check, ExternalLink, Handshake, UserPlus, UserCheck, FlaskConical,
  Mail, Phone, Linkedin, ChevronDown, ChevronUp, Send, X
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import ReportDialog from '@/components/ReportDialog';
import QuickActions from '@/components/QuickActions';
import { toast } from 'sonner';

const BACKEND_URL = '';

function MatchCard({ match, onFollow, onViewProfile, followingSet, onDismiss }) {
  const { user: member, score, reasons } = match;
  const maxScore = 40;
  const pct = Math.min(Math.round((score / maxScore) * 100), 99);
  const isFollowing = followingSet.has(member.user_id);

  return (
    <div data-testid={`match-card-${member.user_id}`} className="p-4 border border-slate-100 rounded-xl hover:border-brand-blue/30 hover:shadow-sm transition-all bg-white cursor-pointer relative h-full flex flex-col"
      onClick={() => onViewProfile(member)}>
      <div className="flex items-start gap-3">
        <div className="relative w-10 h-10 flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue via-brand-navy to-brand-sage flex items-center justify-center text-slate-900 text-sm font-bold absolute inset-0 select-none">
            {member.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          {member.picture && (
            <img src={member.picture} alt={member.name}
              className="w-10 h-10 rounded-full object-cover absolute inset-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold leading-snug line-clamp-2 break-words" title={member.name} data-testid={`match-name-${member.user_id}`}>{member.name}</h4>
          <p className="text-xs text-slate-500 line-clamp-2">{member.profile?.role || 'Member'}</p>
          {(member.company_name || member.profile?.organisation) && (
            <p className="text-[11px] text-slate-400 flex items-start gap-1 mt-0.5"
              data-testid={`match-company-${member.user_id}`}>
              <Building2 className="h-3 w-3 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-2">{member.company_name || member.profile?.organisation}</span>
            </p>
          )}
        </div>
      </div>
      {reasons?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2 ml-[52px]">
          {/* Cap match reasons to the two strongest — long lists clutter the
              card and the top reasons already convey why it's a match. */}
          {reasons.slice(0, 2).map((r, i) => (
            <span key={i} className="text-[10px] text-brand-navy bg-brand-navy/5 px-2 py-0.5 rounded-full">{r}</span>
          ))}
        </div>
      )}
      {/* Action row pinned to the bottom of the card via `mt-auto` so the
          Follow/Ignore buttons line up across every match card even when
          some have more reason tags than others. */}
      <div className="mt-auto pt-3">
        <span className="inline-block text-[10px] font-bold text-brand-navy bg-brand-blue/20 px-2 py-0.5 rounded-full whitespace-nowrap mb-2" data-testid={`match-pct-${member.user_id}`}>{pct}% match</span>
      <div className="flex items-center gap-2">
        <Button
          data-testid={`match-follow-${member.user_id}`}
          size="sm"
          variant={isFollowing ? 'default' : 'outline'}
          className={`flex-1 text-xs ${isFollowing ? 'bg-brand-navy text-white' : ''}`}
          onClick={(e) => { e.stopPropagation(); onFollow(member.user_id); }}
        >
          {isFollowing ? <><UserCheck className="h-3 w-3 mr-1" /> Following</> : <><UserPlus className="h-3 w-3 mr-1" /> Follow</>}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onDismiss(member); }}
          data-testid={`match-dismiss-${member.user_id}`}
          title="Hide from my recommendations"
          className="text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
        >
          <X className="h-3 w-3 mr-1" /> Ignore
        </Button>
      </div>
      </div>
    </div>
  );
}

import { STAKEHOLDER_COLORS } from '@/components/profile/profileConstants';
import MemberProfileModal from '@/components/MemberProfileModal';

function MemberCard({ member, isCurrentUser, onMessage, onReport, onViewProfile, onFollow, isFollowing, navigate }) {
  const stakeholder = member.profile?.background;
  const stakeholderColor = stakeholder ? (STAKEHOLDER_COLORS[stakeholder] || 'bg-slate-100 text-slate-600') : null;

  // Helper to render an info row that ALWAYS occupies the same vertical
  // slot, even when the underlying field is empty. This guarantees a
  // tidy grid alignment across the directory regardless of how complete
  // each member's profile is.
  const Row = ({ icon: Icon, value, testid }) => (
    <div className="flex items-center text-sm text-slate-600 mb-1.5 min-h-[20px]" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
      <span className={`truncate ${value ? '' : 'text-slate-300'}`}>{value || '—'}</span>
    </div>
  );

  return (
    <Card data-testid={`member-card-${member.user_id}`}
      className="p-5 border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer flex flex-col h-full"
      onClick={() => onViewProfile(member)}>
      {/* Avatar + header — fixed height */}
      <div className="flex items-start space-x-4 mb-3 min-h-[70px]">
        <div className="relative w-14 h-14 flex-shrink-0">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-blue via-brand-navy to-brand-sage flex items-center justify-center text-slate-900 text-lg font-bold absolute inset-0 select-none">
            {member.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          {member.picture && (
            <img src={member.picture} alt={member.name}
              className="w-14 h-14 rounded-full object-cover absolute inset-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold font-heading truncate">{member.name}</h3>
          <p className="text-sm text-slate-600 truncate">{member.profile?.role || 'Member'}</p>
          {/* Stakeholder pill slot — always reserved so the next row aligns */}
          <div className="mt-1 min-h-[20px]">
            {stakeholder && (
              <span className={`inline-block text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${stakeholderColor}`} data-testid={`stakeholder-pill-${member.user_id}`}>
                {stakeholder}
              </span>
            )}
          </div>
        </div>
        {!isCurrentUser && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button data-testid={`report-member-${member.user_id}`}
                  onClick={(e) => { e.stopPropagation(); onReport(member); }}
                  className="text-slate-300 hover:text-red-500 transition-colors p-1">
                  <Flag className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Report Member</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Fixed-position info rows (always rendered, placeholder when empty).
          When the member's company is linked to the directory we surface
          it via the chip below instead, to avoid showing "Ligentia UK Ltd"
          twice (org row + Ligentia chip). */}
      {!(member.company_id && member.company_name) && (
        <Row icon={Briefcase} value={member.profile?.organisation} testid={`row-org-${member.user_id}`} />
      )}

      {/* Company / Partner pills — fixed-height slot so the absence of
          a partner badge doesn't squash this card's layout vs others. */}
      <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[26px]">
        {member.company_id && member.company_name ? (
          <>
            <button onClick={(e) => { e.stopPropagation(); navigate(`/companies/${member.company_id}`); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
              data-testid={`company-pill-${member.user_id}`}>
              <Building2 className="h-3 w-3" /> {member.company_name}
            </button>
            {member.company_is_partner && (
              <button onClick={(e) => { e.stopPropagation(); navigate(`/partners/partner_from_${member.company_id}`); }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 transition-colors"
                data-testid={`partner-pill-${member.user_id}`}>
                <Handshake className="h-3 w-3" /> Partner Profile
              </button>
            )}
          </>
        ) : null}
      </div>

      <Row icon={MapPin} value={member.profile?.location} testid={`row-location-${member.user_id}`} />
      <Row icon={Tag} value={member.profile?.sector} testid={`row-sector-${member.user_id}`} />

      {/* Self-description slot — fixed 2-line height */}
      <p className="text-xs text-slate-500 mb-1 line-clamp-2 min-h-[32px]" data-testid={`row-desc-${member.user_id}`}>
        {member.profile?.self_description || <span className="text-slate-300">No description provided yet.</span>}
      </p>

      {/* Expertise chips — fixed-height slot, kept tight so the action bar
          sits close beneath the description like the user requested. */}
      <div className="flex flex-wrap gap-1 mb-2 min-h-[22px]" data-testid={`row-expertise-${member.user_id}`}>
        {member.profile?.expertise?.length > 0 ? (
          <>
            {member.profile.expertise.slice(0, 3).map(e => (
              <Badge key={e} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600">{e}</Badge>
            ))}
            {member.profile.expertise.length > 3 && (
              <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-400">+{member.profile.expertise.length - 3}</Badge>
            )}
          </>
        ) : null}
      </div>

      {/* Action bar pinned to the bottom of the card so the Follow
          button is aligned across the whole grid regardless of how much
          content sits above it. */}
      <div className="mt-auto pt-0">
        {!isCurrentUser ? (
          <div className="flex gap-2">
            <Button data-testid={`follow-member-${member.user_id}`} size="sm"
              variant={isFollowing ? 'default' : 'outline'}
              className={`flex-1 ${isFollowing ? 'bg-brand-navy text-white hover:bg-brand-dark' : ''}`}
              onClick={(e) => { e.stopPropagation(); onFollow(member.user_id); }}>
              {isFollowing ? <><UserCheck className="h-3.5 w-3.5 mr-1.5" /> Following</> : <><UserPlus className="h-3.5 w-3.5 mr-1.5" /> Follow</>}
            </Button>
            <Button data-testid={`message-member-${member.user_id}`} size="sm" variant="outline"
              onClick={(e) => { e.stopPropagation(); onMessage(member); }}>
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          // Empty action-bar slot so the user's own card matches the
          // height of every other card in the grid.
          <div className="h-8" aria-hidden />
        )}
      </div>
    </Card>
  );
}


export default function MemberDirectory({ user }) {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [reportTarget, setReportTarget] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', company: '', email: '' });
  const [inviting, setInviting] = useState(false);

  const token = typeof window !== 'undefined' ? sessionStorage.getItem('session_token') : null;
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/members`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => {
        const sorted = [...data].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setMembers(sorted);
        setFilteredMembers(sorted);
        setLoading(false);
        // Auto-open member profile if ?view= param is present
        const viewId = searchParams.get('view');
        if (viewId) {
          const target = sorted.find(m => m.user_id === viewId);
          if (target) setSelectedMember(target);
        }
      })
      .catch(() => setLoading(false));

    fetch(`${BACKEND_URL}/api/members/smart-match`, { credentials: 'include', headers })
      .then(res => res.json())
      .then(data => { setMatches(data); setMatchesLoading(false); })
      .catch(() => setMatchesLoading(false));

    fetch(`${BACKEND_URL}/api/members/me/following`, { credentials: 'include', headers })
      .then(res => res.ok ? res.json() : [])
      .then(ids => setFollowingSet(new Set(ids)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let result = [...members];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(m =>
        m.name?.toLowerCase().includes(s) ||
        m.profile?.organisation?.toLowerCase().includes(s) ||
        m.profile?.sector?.toLowerCase().includes(s) ||
        m.profile?.role?.toLowerCase().includes(s) ||
        m.profile?.expertise?.some(e => e.toLowerCase().includes(s))
      );
    }
    if (sectorFilter !== 'all') result = result.filter(m => m.profile?.sector === sectorFilter);
    if (locationFilter !== 'all') result = result.filter(m => m.profile?.location === locationFilter);
    result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setFilteredMembers(result);
  }, [search, sectorFilter, locationFilter, members]);

  const handleMessage = (member) => {
    navigate(`/messages?to=${member.user_id}`);
  };

  const handleFollow = async (userId) => {
    const isFollowing = followingSet.has(userId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/members/${userId}/follow`, {
        method: isFollowing ? 'DELETE' : 'POST',
        credentials: 'include', headers,
      });
      if (res.ok) {
        setFollowingSet(prev => {
          const next = new Set(prev);
          if (isFollowing) next.delete(userId); else next.add(userId);
          return next;
        });
        toast.success(isFollowing ? 'Unfollowed' : 'Following — their posts will be prioritised in your forum feed');
      }
    } catch { toast.error('Failed to update follow'); }
  };

  const handleReport = (member) => {
    setReportTarget({ userId: member.user_id, userName: member.name, contextType: 'profile', contextId: member.user_id });
  };

  const handleDismissMatch = async (member) => {
    // Optimistically remove the dismissed match so the next-highest-scoring
    // candidate (already returned by the backend in the top-10 pool) slides
    // into the visible 4-card grid immediately.
    setMatches(prev => prev.filter(m => m.user?.user_id !== member.user_id));
    try {
      const res = await fetch(`${BACKEND_URL}/api/members/smart-match/ignore/${member.user_id}`, {
        method: 'POST', credentials: 'include', headers,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`${member.name} hidden from your matches`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await fetch(`${BACKEND_URL}/api/members/smart-match/restore/${member.user_id}`, {
                method: 'POST', credentials: 'include', headers,
              });
              // Re-fetch matches so the restored member reappears with the
              // correct score & ordering.
              const r = await fetch(`${BACKEND_URL}/api/members/smart-match`, { credentials: 'include', headers });
              if (r.ok) setMatches(await r.json());
              toast.success('Recommendation restored');
            } catch { toast.error('Could not restore'); }
          },
        },
      });
    } catch {
      // Roll back on failure
      toast.error('Could not dismiss — please try again');
      const r = await fetch(`${BACKEND_URL}/api/members/smart-match`, { credentials: 'include', headers });
      if (r.ok) setMatches(await r.json());
    }
  };

  const sectors = [...new Set(members.map(m => m.profile?.sector).filter(Boolean))].sort();
  const locations = [...new Set(members.map(m => m.profile?.location).filter(Boolean))].sort();

  // ── Pagination (max 15 per page) ────────────────────────────────────
  // Switched from "render everything" to a 15-per-page slice with the
  // same compact-buttons treatment as the News grid.
  const MEMBERS_PER_PAGE = 15;
  const [page, setPage] = useState(1);
  const gridTopRef = useRef(null);
  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / MEMBERS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * MEMBERS_PER_PAGE;
  const pageMembers = filteredMembers.slice(pageStart, pageStart + MEMBERS_PER_PAGE);

  // Reset to first page whenever the underlying list changes (search/filter)
  useEffect(() => { setPage(1); }, [search, sectorFilter, locationFilter]);

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    if (gridTopRef.current) gridTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Compact page number list with ellipses for long ranges
  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const out = [];
    let prev = 0;
    Array.from(pages).filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b).forEach(p => {
      if (prev && p - prev > 1) out.push('…');
      out.push(p);
      prev = p;
    });
    return out;
  }, [totalPages, currentPage]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="members" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading mb-1 md:mb-2 gradient-text flex items-center gap-2" data-testid="member-directory-title">
                Members <PageExplainer pageKey="members" label="About Members" />
              </h1>
              <p className="text-slate-600 max-w-2xl">
                Connect with sustainability and logistics professionals across the community.
                Browse, follow, message, and invite others to join in the shared mission of decarbonising transport and logistics.
              </p>
            </div>
            <Button onClick={() => setInviteOpen(true)}
              data-testid="invite-member-btn"
              className="bg-brand-navy hover:bg-brand-dark text-white shadow-sm self-start sm:self-auto">
              <Send className="h-4 w-4 mr-2" />
              Invite a new member
            </Button>
          </div>

          {/* Smart Matches */}
          {matches.length > 0 && (
            <Card className="p-6 mb-8 border-brand-blue/20 bg-gradient-to-r from-white to-brand-navy/5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-brand-navy" />
                <h2 className="text-lg font-bold font-heading">Smart Matches</h2>
                <span className="text-xs text-slate-400">Based on your profile</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {matches.slice(0, 4).map((match, i) => (
                  <MatchCard key={match.user?.user_id || i} match={match} onFollow={handleFollow} onViewProfile={setSelectedMember} followingSet={followingSet} onDismiss={handleDismissMatch} />
                ))}
              </div>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Input data-testid="member-search-input" placeholder="Search by name, role, organisation..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={sectorFilter} onValueChange={setSectorFilter}>
              <SelectTrigger className="w-48" data-testid="sector-filter">
                <SelectValue placeholder="All Sectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-48" data-testid="location-filter">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-slate-500 mb-4" data-testid="member-count">
            {filteredMembers.length} member{filteredMembers.length !== 1 ? 's' : ''}
            {totalPages > 1 && (
              <span className="text-slate-400"> · page {currentPage} of {totalPages}</span>
            )}
          </p>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="p-5 animate-pulse"><div className="flex gap-4"><div className="w-14 h-14 bg-slate-200 rounded-full" /><div className="flex-1"><div className="h-4 bg-slate-200 rounded w-3/4 mb-2" /><div className="h-3 bg-slate-100 rounded w-1/2" /></div></div></Card>
              ))}
            </div>
          ) : (
            <div ref={gridTopRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pageMembers.map(member => (
                <MemberCard
                  key={member.user_id}
                  member={member}
                  isCurrentUser={member.user_id === user?.user_id}
                  onMessage={handleMessage}
                  onReport={handleReport}
                  onViewProfile={setSelectedMember}
                  onFollow={handleFollow}
                  isFollowing={followingSet.has(member.user_id)}
                  navigate={navigate}
                />
              ))}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="members-pagination">
              <p className="text-xs text-slate-500" data-testid="members-pagination-summary">
                Showing <span className="font-semibold text-slate-700">{pageStart + 1}</span>
                –<span className="font-semibold text-slate-700">{Math.min(pageStart + MEMBERS_PER_PAGE, filteredMembers.length)}</span>
                {' '}of <span className="font-semibold text-slate-700">{filteredMembers.length}</span> members
              </p>
              <div className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="members-pagination-prev"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageButtons.map((p, idx) => (
                  p === '…' ? (
                    <span key={`gap-${idx}`} className="px-2 text-slate-400 select-none">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goToPage(p)}
                      data-testid={`members-pagination-page-${p}`}
                      className={`inline-flex items-center justify-center h-9 min-w-9 px-3 rounded-md text-sm font-medium border transition-colors ${
                        p === currentPage
                          ? 'bg-brand-navy text-white border-brand-navy shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                      aria-current={p === currentPage ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  )
                ))}
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="members-pagination-next"
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {!loading && filteredMembers.length === 0 && (
            <Card className="p-12 text-center">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No members match your search</p>
            </Card>
          )}

          <QuickActions user={user} exclude={['/members']} />
            </div>
            <PageSideRail placement="members" />
          </div>
        </div>
      </main>

      {/* Member Profile Modal */}
      <MemberProfileModal
        member={selectedMember}
        open={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        user={user}
        onFollow={handleFollow}
        isFollowing={selectedMember ? followingSet.has(selectedMember.user_id) : false}
      />

      {/* Report Dialog */}
      <ReportDialog
        open={!!reportTarget}
        onOpenChange={() => setReportTarget(null)}
        reportedUserId={reportTarget?.userId}
        reportedUserName={reportTarget?.userName}
        contextType={reportTarget?.contextType}
        contextId={reportTarget?.contextId}
      />

      {/* Invite a new member */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) { setInviteOpen(false); setInviteForm({ name: '', company: '', email: '' }); } }}>
        <DialogContent className="max-w-md" data-testid="invite-member-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-brand-navy" /> Invite a new member</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-2 mb-2">
            They'll receive an email from you with a link to sign up. Once they accept, admins will see it in the invitations audit trail.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
                toast.error('Full name and email are required'); return;
              }
              setInviting(true);
              try {
                const res = await fetch(`${BACKEND_URL}/api/members/invite`, {
                  method: 'POST', credentials: 'include',
                  headers: { ...headers, 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    name: inviteForm.name.trim(),
                    company: inviteForm.company.trim(),
                    email: inviteForm.email.trim().toLowerCase(),
                  }),
                });
                const d = await res.json();
                if (!res.ok) throw new Error(d.detail || 'Failed to send invite');
                toast.success(`Invitation sent to ${d.invitee_name}`);
                setInviteOpen(false);
                setInviteForm({ name: '', company: '', email: '' });
              } catch (err) { toast.error(err.message); }
              setInviting(false);
            }}
            className="space-y-3"
          >
            <div>
              <Label htmlFor="invite-name">Full name <span className="text-red-500">*</span></Label>
              <Input id="invite-name" data-testid="invite-name-input" value={inviteForm.name}
                onChange={e => setInviteForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Alex Morgan" required />
            </div>
            <div>
              <Label htmlFor="invite-company">Company name</Label>
              <Input id="invite-company" data-testid="invite-company-input" value={inviteForm.company}
                onChange={e => setInviteForm(p => ({ ...p, company: e.target.value }))}
                placeholder="e.g. GreenLogistics Ltd" />
            </div>
            <div>
              <Label htmlFor="invite-email">Email address <span className="text-red-500">*</span></Label>
              <Input id="invite-email" type="email" data-testid="invite-email-input" value={inviteForm.email}
                onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                placeholder="alex@company.com" required />
            </div>
            <Button type="submit" disabled={inviting || !inviteForm.name.trim() || !inviteForm.email.trim()}
              data-testid="invite-submit-btn"
              className="w-full bg-brand-navy hover:bg-brand-dark text-white">
              {inviting ? 'Sending…' : 'Send invitation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
